import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html, type PropertyValues } from 'lit'
import { property } from 'lit/decorators.js'
import { when } from 'lit/directives/when.js'

@schmancyElement('schmancy-choice')
export class SchmancyChoice extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				cursor: pointer;
				outline: none;
			}
			:host([disabled]) {
				cursor: not-allowed;
				pointer-events: none;
			}
			:host(:focus-visible) {
				outline: 2px solid var(--schmancy-sys-color-primary-default);
				outline-offset: 2px;
				border-radius: var(--schmancy-radius-lg, 0.625rem); /* mirrors --radius-lg (rounded-lg, line ~65 below) */
			}
		`,
	]

	@property({ type: String }) value = ''
	@property({ type: Boolean }) disabled = false
	@property({ type: Boolean, reflect: true }) selected = false

	private internals = this.attachInternals()

	connectedCallback() {
		super.connectedCallback()
		this.internals.role = 'radio'
		// `ElementInternals`'s ARIA mixin (`internals.role`) is the standards
		// track, but the literal `role` content attribute is what every
		// non-Chromium AT stack, older Chromium/Playwright accessibility-tree
		// reads, and attribute-based test tooling actually reads — internals-only
		// reflection is invisible to all of them. Both are set from the same
		// literal here so they can never disagree.
		this.setAttribute('role', 'radio')
	}

	willUpdate(changed: PropertyValues) {
		super.willUpdate(changed)
		this.internals.ariaChecked = String(this.selected)
		this.internals.ariaDisabled = String(this.disabled)
		this.setAttribute('aria-checked', String(this.selected))
		if (this.disabled) this.setAttribute('aria-disabled', 'true')
		else this.removeAttribute('aria-disabled')
	}

	private fireSelectEvent() {
		if (this.disabled) return
		this.dispatchEvent(
			new CustomEvent<{ value: string }>('schmancy-choice-select', {
				detail: { value: this.value },
				bubbles: true,
				composed: true,
			}),
		)
	}

	render() {
		return html`
			<div
				@click=${this.fireSelectEvent}
				@keydown=${(e: KeyboardEvent) => {
					if (e.key === ' ' || e.key === 'Enter') {
						if (e.key === ' ') e.preventDefault()
						this.fireSelectEvent()
					}
				}}
				class=${this.classMap({
					'flex items-center gap-3 rounded-lg border p-4 transition-colors': true,
					'border-primary-default bg-primary-container': this.selected,
					'border-control-border bg-surface-default': !this.selected,
					'opacity-40': this.disabled,
					'hover:border-primary-default hover:bg-surface-containerHigh': !this.selected && !this.disabled,
				})}
			>
				<div class="flex-none flex items-center justify-center">
					<slot name="leading">
						<span
							class=${this.classMap({
								'inline-flex items-center justify-center w-4.5 h-4.5 rounded-full border-2 transition-colors': true,
								'border-primary-default': this.selected,
								'border-outline': !this.selected,
							})}
						>
							${when(this.selected, () => html`<span class="w-2 h-2 rounded-full bg-primary-default"></span>`)}
						</span>
					</slot>
				</div>

				<div class="flex-1 min-w-0 flex flex-col gap-1">
					<slot name="title"></slot>
					<slot name="evidence"></slot>
					<slot></slot>
				</div>

				<div class="flex-none">
					<slot name="trailing"></slot>
				</div>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-choice': SchmancyChoice
	}
}
